import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import {
  CheckCircle2,
  X,
  Heart,
  Star,
  ArrowRight,
  Trophy,
  RotateCcw,
  BookOpen,
  Flame
} from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { coursesAPI, progressAPI, authAPI } from '../services/api';
import { LogoIcon } from '../components/Logo';
import { useLanguage } from '../i18n/LanguageContext';
import { useGameSounds } from '../hooks/useGameSounds';
import Confetti from '../components/Confetti';
import ComboMessage from '../components/ComboMessage';
import XPAnimation from '../components/XPAnimation';
import HeartLostAnimation from '../components/HeartLostAnimation';
import LessonComplete from '../components/LessonComplete';
import Mascot from '../components/Mascot';
import { getLocalizedExercise, getLocalizedLesson } from '../utils/languageUtils';

const LessonNew = () => {
  const navigate = useNavigate();
  const { lessonId } = useParams();
  const [searchParams] = useSearchParams();
  const courseSlug = searchParams.get('course') || 'islam';
  const { t, language } = useLanguage();
  
  // Hook pour les sons
  const { playCorrect, playIncorrect, playCombo, playHeartLost, playClick } = useGameSounds();

  const [lesson, setLesson] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  
  // Localiser la leçon et les exercices selon la langue sélectionnée
  const localizedLesson = useMemo(() => getLocalizedLesson(lesson, language), [lesson, language]);
  const localizedExercises = useMemo(() => 
    exercises.map(ex => getLocalizedExercise(ex, language)), 
    [exercises, language]
  );
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [user, setUser] = useState(null);
  const [totalXPGained, setTotalXPGained] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [heartLost, setHeartLost] = useState(false);
  const [allLessons, setAllLessons] = useState([]);
  
  // États simplifiés
  const [showSummary, setShowSummary] = useState(false); // Écran récapitulatif
  const [failedLesson, setFailedLesson] = useState(false); // 0 cœur = échec
  
  // États pour les animations Duolingo-like
  const [streak, setStreak] = useState(0); // Combo de bonnes réponses
  const [showCombo, setShowCombo] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showXPAnimation, setShowXPAnimation] = useState(false);
  const [currentXPGain, setCurrentXPGain] = useState(0);
  const [showHeartLostAnim, setShowHeartLostAnim] = useState(false);
  const [lessonStartTime, setLessonStartTime] = useState(null);
  const [showLessonComplete, setShowLessonComplete] = useState(false);
  
  // État pour la mascotte
  const [showMascot, setShowMascot] = useState(false);
  const [mascotScore, setMascotScore] = useState(0);
  
  // États pour le mode révision des erreurs
  const [wrongAnswers, setWrongAnswers] = useState([]); // Liste des exercices ratés
  const [isReviewMode, setIsReviewMode] = useState(false); // Mode révision actif
  const [reviewIndex, setReviewIndex] = useState(0); // Index dans la révision
  const [showReviewIntro, setShowReviewIntro] = useState(false); // Écran d'intro révision
  const [initialCorrectCount, setInitialCorrectCount] = useState(0); // Score initial AVANT révision
  const [initialXPGained, setInitialXPGained] = useState(0); // XP initial AVANT révision
  const [reviewWrongAnswers, setReviewWrongAnswers] = useState([]); // Erreurs commises PENDANT la révision
  
  // Localiser les wrongAnswers pour le mode révision
  const localizedWrongAnswers = useMemo(() => 
    wrongAnswers.map(ex => getLocalizedExercise(ex, language)), 
    [wrongAnswers, language]
  );

  const loadLesson = useCallback(async () => {
    try {
      // Reset states when loading a new lesson
      setLoading(true);
      setCurrentExerciseIndex(0);
      setSelectedAnswer(null);
      setShowFeedback(false);
      setIsCorrect(false);
      setTotalXPGained(0);
      setCorrectAnswers(0);
      setShowSummary(false);
      setFailedLesson(false);
      setHeartLost(false);
      
      // Reset animation states
      setStreak(0);
      setShowCombo(false);
      setShowConfetti(false);
      setShowXPAnimation(false);
      setShowHeartLostAnim(false);
      setShowLessonComplete(false);
      setLessonStartTime(Date.now());
      
      // Reset review mode states
      setWrongAnswers([]);
      setIsReviewMode(false);
      setReviewIndex(0);
      setShowReviewIntro(false);
      setReviewWrongAnswers([]);
      
      const [lessonData, exercisesData, userData, lessonsData] = await Promise.all([
        coursesAPI.getLesson(courseSlug, lessonId),
        coursesAPI.getExercises(courseSlug, lessonId),
        authAPI.getCurrentUser(),
        coursesAPI.getLessons(courseSlug)
      ]);

      // Validate exercises data
      if (!exercisesData || !Array.isArray(exercisesData) || exercisesData.length === 0) {
        console.error('❌ No exercises found for lesson:', lessonId);
        // Try to reload exercises once more
        const retryExercises = await coursesAPI.getExercises(courseSlug, lessonId);
        if (retryExercises && Array.isArray(retryExercises) && retryExercises.length > 0) {
          setExercises(retryExercises);
          console.log('✅ Exercises loaded on retry:', retryExercises.length);
        } else {
          console.error('❌ Still no exercises after retry');
          setExercises([]);
        }
      } else {
        setExercises(exercisesData);
        console.log('✅ Exercises loaded:', exercisesData.length);
      }

      setLesson(lessonData);
      setUser(userData);
      setAllLessons(lessonsData);
      
      // Vérifier s'il y a un état de complétion sauvegardé
      const savedCompletion = localStorage.getItem(`lesson_completion_${lessonId}`);
      if (savedCompletion) {
        try {
          const state = JSON.parse(savedCompletion);
          // Vérifier si la complétion date de moins de 24h
          const isRecent = (Date.now() - state.timestamp) < (24 * 60 * 60 * 1000);
          
          if (isRecent) {
            console.log('🔄 Restoring completion state');
            setShowSummary(true);
            setTotalXPGained(state.totalXPGained);
            setCorrectAnswers(state.correctAnswers);
            // Ne pas restaurer les hearts car ils ont peut-être été rechargés
          } else {
            // Nettoyer les anciennes données
            localStorage.removeItem(`lesson_completion_${lessonId}`);
          }
        } catch (e) {
          console.error('Error restoring completion state:', e);
        }
      }
      
      // Vérifier s'il y a un état de progression en cours sauvegardé
      const savedProgress = localStorage.getItem(`lesson_progress_${lessonId}`);
      if (savedProgress && !savedCompletion) {
        try {
          const progressState = JSON.parse(savedProgress);
          // Vérifier si la progression date de moins de 2h
          const isRecent = (Date.now() - progressState.timestamp) < (2 * 60 * 60 * 1000);
          
          if (isRecent && progressState.currentIndex < exercisesData.length) {
            console.log('🔄 Restoring progress state - Question', progressState.currentIndex + 1);
            setCurrentExerciseIndex(progressState.currentIndex);
            setCorrectAnswers(progressState.correctAnswers || 0);
            setTotalXPGained(progressState.totalXPGained || 0);
            setWrongAnswers(progressState.wrongAnswers || []);
          } else {
            // Nettoyer les anciennes données
            localStorage.removeItem(`lesson_progress_${lessonId}`);
          }
        } catch (e) {
          console.error('Error restoring progress state:', e);
          localStorage.removeItem(`lesson_progress_${lessonId}`);
        }
      }
      
      setLoading(false);
      console.log('✅ Lesson loaded:', lessonData.title, `(Order: ${lessonData.order})`);
    } catch (error) {
      console.error('Error loading lesson:', error);
      setLoading(false);
    }
  }, [courseSlug, lessonId]);

  useEffect(() => {
    loadLesson();
  }, [loadLesson]);

  // Surveiller les cœurs et déclencher l'échec à 0
  useEffect(() => {
    if (user && user.hearts === 0 && !failedLesson) {
      console.log('0 hearts detected - triggering failure screen');
      setFailedLesson(true);
    }
  }, [user?.hearts, failedLesson]);

  const handleAnswerSelect = async (answer) => {
    if (showFeedback) return;
    
    // Bloquer si leçon échouée
    if (failedLesson) return;
    
    // Son de clic
    playClick();
    
    setSelectedAnswer(answer);
    
    // Déterminer l'exercice courant selon le mode (utiliser les versions NON localisées pour l'API)
    const currentEx = isReviewMode 
      ? wrongAnswers[reviewIndex] 
      : exercises[currentExerciseIndex];

    // Vérification de sécurité - si l'exercice n'existe pas, ne rien faire
    if (!currentEx || !currentEx.id) {
      console.error('❌ No current exercise found!', { isReviewMode, reviewIndex, wrongAnswersLength: wrongAnswers.length });
      return;
    }

    try {
      console.log('Submitting answer:', { lessonId, exerciseId: currentEx.id, answer, language, isReviewMode });
      const result = await progressAPI.submitAnswer(
        lessonId,
        currentEx.id,
        answer,
        language
      );
      console.log('Answer result:', result);

      setIsCorrect(result.correct);
      setShowFeedback(true);
      
      // Track heart loss
      const previousHearts = user?.hearts || 5;
      setUser(result.user_stats);
      
      if (result.correct) {
        // 🎉 Bonne réponse !
        playCorrect();
        
        // Incrémenter le streak
        const newStreak = streak + 1;
        setStreak(newStreak);
        
        // Afficher animation XP
        const xpGain = result.xp_earned || Math.floor((lesson?.xp || 100) / localizedExercises.length);
        setCurrentXPGain(xpGain);
        setShowXPAnimation(true);
        setTimeout(() => setShowXPAnimation(false), 1000);
        
        // Afficher confettis pour bonne réponse
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 1500);
        
        // Afficher message de combo à 3, 5, 7, 10 bonnes réponses
        if ([3, 5, 7, 10].includes(newStreak)) {
          playCombo();
          setShowCombo(true);
          setTimeout(() => setShowCombo(false), 2500);
        }
        
        setTotalXPGained(prev => prev + result.xp_earned);
        // Incrémenter le score seulement si pas en mode révision
        if (!isReviewMode) {
          setCorrectAnswers(prev => prev + 1);
        }
      } else {
        // ❌ Mauvaise réponse
        playIncorrect();
        
        // Reset le streak
        setStreak(0);
        
        // Enregistrer l'erreur pour le mode révision
        if (!isReviewMode) {
          // Première passe : enregistrer pour la révision initiale
          setWrongAnswers(prev => {
            // Éviter les doublons
            if (!prev.find(e => e.id === currentEx.id)) {
              return [...prev, currentEx];
            }
            return prev;
          });
        } else {
          // Mode révision : enregistrer les erreurs persistantes pour éventuelle re-révision
          setReviewWrongAnswers(prev => {
            if (!prev.find(e => e.id === currentEx.id)) {
              return [...prev, currentEx];
            }
            return prev;
          });
        }
        
        if (result.hearts_remaining < previousHearts) {
          // Animation de perte de cœur
          playHeartLost();
          setHeartLost(true);
          setShowHeartLostAnim(true);
          setTimeout(() => {
            setHeartLost(false);
            setShowHeartLostAnim(false);
          }, 2000);
        }
      }
      
      // Vérifier échec (0 cœur)
      if (result.hearts_remaining === 0) {
        setFailedLesson(true);
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
    }
  };

  const handleNextExercise = async () => {
    // Si échec (0 cœur), ne pas continuer
    if (failedLesson) {
      return;
    }
    
    // Si on est en mode révision
    if (isReviewMode) {
      if (reviewIndex < localizedWrongAnswers.length - 1) {
        setReviewIndex(prev => prev + 1);
        setSelectedAnswer(null);
        setShowFeedback(false);
        setIsCorrect(false);
      } else {
        // Fin de la révision
        // Si l'utilisateur a encore fait des erreurs pendant la révision, on recommence avec ces erreurs
        if (reviewWrongAnswers.length > 0) {
          // Recommencer la révision avec seulement les erreurs persistantes
          setWrongAnswers(reviewWrongAnswers);
          setReviewWrongAnswers([]);
          setReviewIndex(0);
          setSelectedAnswer(null);
          setShowFeedback(false);
          setIsCorrect(false);
          setShowReviewIntro(true); // Montrer l'intro de révision avec le nouveau compte
          setIsReviewMode(false);
        } else {
          // Pas d'erreurs pendant la révision - afficher l'écran de complétion vert
          setIsReviewMode(false);
          setShowLessonComplete(true);
        }
      }
      return;
    }
    
    if (currentExerciseIndex < localizedExercises.length - 1) {
      const nextIndex = currentExerciseIndex + 1;
      setCurrentExerciseIndex(nextIndex);
      setSelectedAnswer(null);
      setShowFeedback(false);
      setIsCorrect(false);
      setHeartLost(false);
      
      // Sauvegarder la progression en cours pour reprise
      const progressState = {
        currentIndex: nextIndex,
        correctAnswers: correctAnswers,
        totalXPGained: totalXPGained,
        wrongAnswers: wrongAnswers,
        timestamp: Date.now()
      };
      localStorage.setItem(`lesson_progress_${lessonId}`, JSON.stringify(progressState));
      console.log('💾 Progress saved - Question', nextIndex + 1);
    } else {
      // Fin de tous les exercices
      const isPerfect = correctAnswers === localizedExercises.length;
      
      // Sauvegarder le score initial AVANT la révision
      setInitialCorrectCount(correctAnswers);
      setInitialXPGained(totalXPGained);
      
      // S'il y a des erreurs et qu'on n'est pas en mode révision, afficher l'écran d'intro révision
      if (wrongAnswers.length > 0) {
        setShowReviewIntro(true);
      } else {
        // Pas d'erreurs - afficher le hibou d'abord, puis la page finale
        const scorePercent = Math.round((correctAnswers / localizedExercises.length) * 100);
        setMascotScore(scorePercent);
        setShowMascot(true);
        
        if (isPerfect) {
          playCombo();
          setShowCombo(true);
        }
      }
      
      // Sauvegarder l'état de complétion pour permettre de revenir
      const completionState = {
        lessonId,
        courseSlug,
        totalXPGained,
        correctAnswers,
        hearts: user?.hearts || 5,
        timestamp: Date.now()
      };
      localStorage.setItem(`lesson_completion_${lessonId}`, JSON.stringify(completionState));
      console.log('💾 Lesson completion state saved');
    }
  };
  
  // Démarrer le mode révision
  const startReviewMode = () => {
    setShowReviewIntro(false);
    setIsReviewMode(true);
    setReviewIndex(0);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setIsCorrect(false);
    setReviewWrongAnswers([]); // Reset les erreurs de révision
  };
  
  // Passer la révision et aller au résumé
  const skipReview = () => {
    setShowReviewIntro(false);
    setShowSummary(true);
  };

  const getNextLesson = () => {
    if (!lesson || !allLessons.length) {
      console.log('❌ getNextLesson: Missing data', { lesson, allLessonsCount: allLessons.length });
      return null;
    }
    
    const currentOrder = lesson.order;
    const nextLesson = allLessons.find(l => l.order === currentOrder + 1);
    
    console.log('📊 getNextLesson:', { currentOrder, nextLesson: nextLesson ? `Leçon ${nextLesson.order}: ${nextLesson.title}` : 'None' });
    
    return nextLesson;
  };

  const handleCompleteLessonSuccess = async () => {
    try {
      const token = localStorage.getItem('spirify_token');
      await fetch(
        `${window.location.origin}/api/progress/complete-lesson/${lessonId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('✅ Lesson marked as completed');
    } catch (error) {
      console.error('Error marking lesson as completed:', error);
    }
    
    // Clean up completion state and progress when going to dashboard
    localStorage.removeItem(`lesson_completion_${lessonId}`);
    localStorage.removeItem(`lesson_progress_${lessonId}`);
    
    navigate('/dashboard');
  };

  const handleNextLesson = async () => {
    console.log('🚀 handleNextLesson called');
    const nextLesson = getNextLesson();
    
    if (!nextLesson) {
      console.log('📍 No next lesson, going to dashboard');
      // No next lesson, go to dashboard
      await handleCompleteLessonSuccess();
      return;
    }

    console.log(`➡️  Navigating to next lesson: ${nextLesson.title}`);

    try {
      // Mark current lesson as completed
      const token = localStorage.getItem('spirify_token');
      const response = await fetch(
        `${window.location.origin}/api/progress/complete-lesson/${lessonId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        console.log('✅ Lesson marked as completed');
      } else {
        console.log('⚠️  Failed to mark lesson as completed:', response.status);
      }
      
      // Clean up completion state and progress for current lesson
      localStorage.removeItem(`lesson_completion_${lessonId}`);
      localStorage.removeItem(`lesson_progress_${lessonId}`);
      
      // CRITICAL FIX: Also clean up any saved state for the next lesson
      // This prevents the next lesson from immediately showing summary screen
      localStorage.removeItem(`lesson_completion_${nextLesson.id}`);
      localStorage.removeItem(`lesson_progress_${nextLesson.id}`);
      
      // Navigate to next lesson
      console.log(`🔀 Navigating to: /lesson/${nextLesson.id}?course=${courseSlug}`);
      navigate(`/lesson/${nextLesson.id}?course=${courseSlug}`);
    } catch (error) {
      console.error('❌ Error marking lesson as completed:', error);
      // Clean up completion state and progress for current lesson
      localStorage.removeItem(`lesson_completion_${lessonId}`);
      localStorage.removeItem(`lesson_progress_${lessonId}`);
      
      // CRITICAL FIX: Also clean up any saved state for the next lesson
      // This prevents the next lesson from immediately showing summary screen
      localStorage.removeItem(`lesson_completion_${nextLesson.id}`);
      localStorage.removeItem(`lesson_progress_${nextLesson.id}`);
      
      // Still navigate to next lesson even if completion fails
      console.log('🔀 Navigating anyway to:', `/lesson/${nextLesson.id}?course=${courseSlug}`);
      navigate(`/lesson/${nextLesson.id}?course=${courseSlug}`);
    }
  };

  const restartLesson = async () => {
    try {
      // Restaurer les cœurs via l'API
      const token = localStorage.getItem('spirify_token');
      await fetch(`${window.location.origin}/api/auth/restore-hearts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Recharger les données de l'utilisateur et réinitialiser l'état
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      
      // Réinitialiser les états
      setCurrentExerciseIndex(0);
      setSelectedAnswer(null);
      setShowFeedback(false);
      setIsCorrect(false);
      setTotalXPGained(0);
      setCorrectAnswers(0);
      setFailedLesson(false);
      setHeartLost(false);
      setShowSummary(false);
      
    } catch (error) {
      console.error('Error restarting lesson:', error);
      // En cas d'erreur, recharger la page
      window.location.reload();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">{t.lesson.loading}</p>
        </div>
      </div>
    );
  }

  // ÉCRAN D'ERREUR (pas d'exercices)
  if (!loading && exercises.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md shadow-2xl border-4 border-orange-500">
          <CardContent className="p-8 text-center">
            <div className="mb-6">
              <BookOpen className="w-20 h-20 text-orange-500 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-orange-600 mb-2">
                {t?.lesson?.noExercises || "Exercices non disponibles"}
              </h1>
              <p className="text-gray-600 mb-4">
                {t?.lesson?.noExercisesMessage || "Impossible de charger les exercices. Veuillez réessayer."}
              </p>
            </div>
            <div className="space-y-3">
              <Button
                onClick={() => window.location.reload()}
                className="w-full py-6 text-lg bg-gradient-to-r from-orange-600 to-yellow-600 hover:from-orange-700 hover:to-yellow-700"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                {t?.lesson?.retry || "Réessayer"}
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="w-full py-6 text-lg"
              >
                {t?.lesson?.backToDashboard || "Retour au tableau de bord"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ÉCRAN D'ÉCHEC (0 cœur)
  if (failedLesson) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md shadow-2xl border-4 border-red-500">
          <CardContent className="p-8 text-center">
            <div className="mb-6">
              <Heart className="w-20 h-20 text-red-500 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-red-600 mb-2">{t.lesson.noHearts}</h1>
              <p className="text-gray-600 mb-4">
                {t.lesson.noHeartsMessage}
              </p>
              <div className="bg-red-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-red-700">
                  💡 <strong>{t.lesson.tip}:</strong> {t.lesson.tipMessage}
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <Button
                onClick={restartLesson}
                className="w-full py-6 text-lg bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                {t.lesson.restart}
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="w-full py-6 text-lg"
              >
                {t.lesson.backToDashboard}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ÉCRAN D'INTRODUCTION À LA RÉVISION DES ERREURS
  if (showReviewIntro) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-2xl border-2 border-orange-400">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 mx-auto bg-orange-100 rounded-full flex items-center justify-center mb-6">
              <RotateCcw className="w-10 h-10 text-orange-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-orange-800 mb-3">
              {t?.lesson?.reviewMode || "Revoyons les erreurs !"}
            </h2>
            
            <p className="text-gray-600 mb-6">
              {t?.lesson?.reviewIntro || `Tu as fait ${localizedWrongAnswers.length} erreur${localizedWrongAnswers.length > 1 ? 's' : ''}. Pas de souci ! Revoyons ensemble ces questions.`}
            </p>
            
            <div className="bg-orange-50 rounded-lg p-4 mb-6">
              <p className="text-orange-700 font-medium">
                <span className="text-2xl font-bold">{localizedWrongAnswers.length}</span>
                <span className="text-sm ml-2">{t?.lesson?.questionsToReview || "question(s) à revoir"}</span>
              </p>
            </div>
            
            <div className="space-y-3">
              <Button
                onClick={startReviewMode}
                className="w-full py-4 text-lg bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                {t?.lesson?.startReview || "Commencer la révision"}
              </Button>
              <Button
                onClick={skipReview}
                variant="ghost"
                className="w-full text-gray-500 hover:text-gray-700"
              >
                {t?.lesson?.skipReview || "Passer et voir le résumé"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ÉCRAN DE COMPLÉTION DUOLINGO-STYLE
  if (showLessonComplete) {
    // Utiliser le score initial si on vient de la révision, sinon le score actuel
    const displayCorrectAnswers = initialCorrectCount > 0 ? initialCorrectCount : correctAnswers;
    const displayXPGained = initialXPGained > 0 ? initialXPGained : totalXPGained;
    const isPerfect = displayCorrectAnswers === localizedExercises.length;
    const timeSpent = 0; // On ne suit plus le temps pour simplifier
    
    return (
      <>
        <ComboMessage streak={streak} show={showCombo && isPerfect} isPerfect={isPerfect} t={t} />
        <LessonComplete
          xpEarned={displayXPGained}
          correctAnswers={displayCorrectAnswers}
          totalQuestions={localizedExercises.length}
          timeSpent={timeSpent}
          onNextLesson={handleNextLesson}
          onBackToDashboard={handleCompleteLessonSuccess}
          hasNextLesson={!!getNextLesson()}
          lessonTitle={localizedLesson?.title}
          lessonContent={localizedLesson?.educational_content || localizedLesson?.content || localizedLesson?.description}
          t={t}
        />
      </>
    );
  }

  // Summary Screen - Afficher le contenu éducatif (backup, rarement utilisé maintenant)
  if (showSummary) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-white p-2 sm:p-4">
        <div className="max-w-4xl mx-auto py-4 sm:py-8">
          <Card className="shadow-2xl border-2 sm:border-4 border-green-500">
            <CardContent className="p-4 sm:p-8">
              {/* Success Header */}
              <div className="text-center mb-6 sm:mb-8">
                <Trophy className="w-16 h-16 sm:w-24 sm:h-24 text-green-500 mx-auto mb-4" />
                <h1 className="text-2xl sm:text-4xl font-bold text-green-600 mb-2">
                  {t?.lesson?.wellDone || "Bravo !"}
                </h1>
                <p className="text-base sm:text-xl text-gray-600">
                  {t?.lesson?.lessonCompleted || "Leçon terminée"}
                </p>
              </div>

              {/* Stats - Responsive grid */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6 sm:mb-8">
                <div className="bg-purple-50 p-2 sm:p-4 rounded-lg text-center">
                  <Star className="w-5 h-5 sm:w-8 sm:h-8 text-purple-600 mx-auto mb-1 sm:mb-2" />
                  <p className="text-lg sm:text-2xl font-bold text-purple-600">{totalXPGained}</p>
                  <p className="text-xs sm:text-sm text-gray-600">XP</p>
                </div>
                <div className="bg-green-50 p-2 sm:p-4 rounded-lg text-center">
                  <CheckCircle2 className="w-5 h-5 sm:w-8 sm:h-8 text-green-600 mx-auto mb-1 sm:mb-2" />
                  <p className="text-lg sm:text-2xl font-bold text-green-600">{correctAnswers}/{localizedExercises.length}</p>
                  <p className="text-xs sm:text-sm text-gray-600">{t.lesson.correct || "Correct"}</p>
                </div>
                <div className="bg-red-50 p-2 sm:p-4 rounded-lg text-center">
                  <Heart className="w-5 h-5 sm:w-8 sm:h-8 text-red-600 mx-auto mb-1 sm:mb-2" />
                  <p className="text-lg sm:text-2xl font-bold text-red-600">{user?.hearts || 0}</p>
                  <p className="text-xs sm:text-sm text-gray-600">{t.lesson.heartsLeft || "Cœurs"}</p>
                </div>
              </div>

              {/* Educational Content */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-3 sm:p-6 mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-2xl font-bold text-blue-900 mb-3 sm:mb-4 flex items-center">
                  <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                  {t?.lesson?.learnMore || "Pour aller plus loin"}
                </h2>
                
                {/* Note: Educational content is in French */}
                {language === 'en' && (
                  <div className="bg-blue-100 border-2 border-blue-300 rounded-lg p-3 mb-3">
                    <div className="flex items-start gap-2">
                      <span className="text-xl">🌍</span>
                      <div>
                        <p className="font-semibold text-blue-900 mb-1 text-sm">Content in French</p>
                        <p className="text-xs text-blue-800">
                          The detailed lesson content below is currently available in French only. 
                          You can use your browser's built-in translation feature (right-click → Translate to English) 
                          for an instant translation, or continue learning with the French text.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="prose prose-sm sm:prose-lg max-w-none">
                  {localizedLesson?.educational_content?.split('\n\n').map((paragraph, idx) => (
                    paragraph.trim() && (
                      <p key={idx} className="text-gray-700 mb-2 sm:mb-3 leading-relaxed text-sm sm:text-base">
                        {paragraph}
                      </p>
                    )
                  ))}
                </div>
              </div>

              {/* Action Buttons - Responsive */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="w-full sm:flex-1 py-4 sm:py-6 text-base sm:text-lg"
                >
                  {t.lesson.backToDashboard || "Retour au tableau de bord"}
                </Button>
                {getNextLesson() ? (
                  <Button
                    onClick={handleNextLesson}
                    className="w-full sm:flex-1 py-4 sm:py-6 text-base sm:text-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                  >
                    {"Leçon suivante"}
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleCompleteLessonSuccess}
                    className="w-full sm:flex-1 py-4 sm:py-6 text-base sm:text-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                  >
                    {t.lesson.continue || "Continuer"}
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const currentExercise = localizedExercises[currentExerciseIndex];
  const progress = ((currentExerciseIndex + 1) / localizedExercises.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      {/* Animations globales */}
      <Confetti active={showConfetti} duration={1500} />
      <ComboMessage streak={streak} show={showCombo} t={t} />
      <XPAnimation xp={currentXPGain} show={showXPAnimation} />
      <HeartLostAnimation show={showHeartLostAnim} />
      
      {/* Mascotte de fin de leçon */}
      <Mascot 
        score={mascotScore} 
        isVisible={showMascot} 
        onClose={() => {
          setShowMascot(false);
          setShowLessonComplete(true);
        }}
        t={t}
      />
      
      {/* Indicateur de streak */}
      {streak >= 2 && (
        <div className="fixed top-20 left-4 z-40 bg-orange-100 text-orange-600 px-3 py-2 rounded-full flex items-center gap-2 shadow-lg animate-pulse">
          <Flame className="w-5 h-5" fill="currentColor" />
          <span className="font-bold">{streak}</span>
        </div>
      )}
      
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center space-x-4 flex-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
            >
              <X className="w-5 h-5" />
            </Button>
            
            <button 

              onClick={() => navigate('/')}
              className="hover:opacity-80 transition-opacity cursor-pointer"
            >
              <LogoIcon className="w-7 h-7" />
            </button>

            <div className="flex-1 max-w-2xl">
              <Progress value={progress} className="h-3" />
            </div>

            <div className="flex items-center space-x-2 relative">
              {[...Array(5)].map((_, i) => (
                <Heart
                  key={i}
                  className={`w-5 h-5 transition-all ${
                    i < (user?.hearts || 0)
                      ? 'text-red-500 fill-red-500'
                      : 'text-gray-300'
                  } ${heartLost && i === user?.hearts ? 'animate-ping' : ''}`}
                />
              ))}
              {heartLost && (
                <span className="absolute -top-8 right-0 text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded shadow-lg animate-bounce">
                  -1 ❤️
                </span>
              )}
            </div>

            <div className="flex items-center space-x-1 text-amber-600 font-bold">
              <Star className="w-5 h-5" />
              <span>{user?.xp || 0}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Mode révision indicator */}
        {isReviewMode && (
          <div className="mb-4 bg-orange-100 border border-orange-300 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <RotateCcw className="w-6 h-6 text-orange-600" />
              <div>
                <p className="font-bold text-orange-800">{t?.lesson?.reviewMode || "Mode Révision"}</p>
                <p className="text-sm text-orange-600">Question {reviewIndex + 1} / {localizedWrongAnswers.length}</p>
              </div>
            </div>
            <Badge className="bg-orange-500 text-white">{localizedWrongAnswers.length} erreur(s)</Badge>
          </div>
        )}
        
        {!showFeedback ? (
          /* Question Card - Design Duolingo amélioré */
          <Card className="shadow-2xl border-0 overflow-hidden">
            {/* Header coloré selon le cours */}
            <div className={`h-2 ${
              courseSlug === 'islam' ? 'bg-gradient-to-r from-emerald-500 to-teal-500' :
              courseSlug === 'christianisme' ? 'bg-gradient-to-r from-purple-500 to-pink-500' :
              courseSlug === 'judaisme' ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
              courseSlug === 'bouddhisme' ? 'bg-gradient-to-r from-yellow-500 to-amber-500' :
              courseSlug === 'hindouisme' ? 'bg-gradient-to-r from-orange-500 to-red-500' :
              courseSlug === 'taoisme' ? 'bg-gradient-to-r from-cyan-500 to-blue-500' :
              'bg-gradient-to-r from-amber-500 to-orange-500'
            }`} />
            
            <CardContent className="p-5 sm:p-8">
              {/* Hearts visible - Style Duolingo */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Heart
                      key={i}
                      className={`w-5 h-5 transition-all duration-300 ${
                        i < (user?.hearts || 0)
                          ? 'text-red-500 fill-red-500'
                          : 'text-gray-200'
                      } ${heartLost && i === user?.hearts ? 'animate-ping' : ''}`}
                    />
                  ))}
                </div>
                
                {/* Progress dots */}
                <div className="flex items-center gap-1">
                  {localizedExercises.slice(0, 8).map((_, idx) => (
                    <div 
                      key={idx}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        idx < currentExerciseIndex ? 'bg-green-500' :
                        idx === currentExerciseIndex ? 'bg-purple-500 scale-125' :
                        'bg-gray-200'
                      }`}
                    />
                  ))}
                  {localizedExercises.length > 8 && (
                    <span className="text-xs text-gray-400 ml-1">...</span>
                  )}
                </div>
              </div>

              {/* Micro-texte de contexte */}
              <div className="mb-4">
                <p className="text-sm text-gray-500 font-medium">
                  {isReviewMode 
                    ? `🔄 ${t?.lesson?.reviewMode || "Mode révision"} • ${reviewIndex + 1}/${localizedWrongAnswers.length}`
                    : `📜 ${localizedLesson?.title || 'Leçon'}`
                  }
                </p>
              </div>

              {/* "À toi de jouer" */}
              <div className="mb-6">
                <p className="text-lg font-bold text-purple-600">
                  {t?.lesson?.yourTurn || "À toi de jouer"} {streak >= 2 && <span className="text-orange-500">🔥 x{streak}</span>}
                </p>
              </div>

              {/* Question */}
              <div className="mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 leading-relaxed">
                  {isReviewMode ? localizedWrongAnswers[reviewIndex]?.question : currentExercise?.question}
                </h2>
              </div>

              {/* Answer Options - Style compact avec icônes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(isReviewMode ? localizedWrongAnswers[reviewIndex]?.options : currentExercise?.options)?.map((option, index) => {
                  const optionIcons = ['🅰️', '🅱️', '©️', '🅳'];
                  const letterIcons = ['A', 'B', 'C', 'D'];
                  
                  return (
                    <button
                      key={index}
                      onClick={() => {
                        playClick();
                        setSelectedAnswer(option);
                      }}
                      disabled={showFeedback}
                      className={`
                        group relative p-4 rounded-xl border-2 text-left transition-all duration-200
                        ${selectedAnswer === option
                          ? 'border-purple-500 bg-purple-50 shadow-lg scale-[1.02]'
                          : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50/50 hover:shadow-md'
                        }
                        ${showFeedback ? 'cursor-not-allowed' : 'cursor-pointer active:scale-[0.98]'}
                      `}
                    >
                      <div className="flex items-center gap-3">
                        {/* Letter badge */}
                        <div className={`
                          w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm
                          transition-all duration-200
                          ${selectedAnswer === option
                            ? 'bg-purple-500 text-white'
                            : 'bg-gray-100 text-gray-500 group-hover:bg-purple-100 group-hover:text-purple-600'
                          }
                        `}>
                          {letterIcons[index]}
                        </div>
                        
                        {/* Answer text */}
                        <span className={`
                          flex-1 font-medium text-base
                          ${selectedAnswer === option ? 'text-purple-700' : 'text-gray-700'}
                        `}>
                          {option}
                        </span>
                        
                        {/* Selected indicator */}
                        {selectedAnswer === option && (
                          <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center">
                            <CheckCircle2 className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Submit Button */}
              <div className="mt-6">
                <Button
                  onClick={() => handleAnswerSelect(selectedAnswer)}
                  disabled={!selectedAnswer}
                  className={`
                    w-full py-6 text-lg font-bold rounded-xl transition-all duration-300
                    ${selectedAnswer
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg hover:shadow-xl'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }
                  `}
                >
                  {t?.lesson?.checkAnswer || "Vérifier"}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Feedback Card - Style Duolingo amélioré */
          <Card className={`shadow-2xl transition-all duration-300 overflow-hidden ${
            isCorrect 
              ? 'border-0 ring-4 ring-green-400 ring-opacity-50' 
              : 'border-0 ring-4 ring-red-400 ring-opacity-50'
          }`}>
            {/* Barre de couleur en haut */}
            <div className={`h-2 ${isCorrect ? 'bg-green-500' : 'bg-red-500'}`} />
            
            <CardContent className="p-6 sm:p-8">
              {/* Feedback immédiat - Compact */}
              <div className={`p-4 rounded-xl mb-6 ${isCorrect ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="flex items-center gap-4">
                  {isCorrect ? (
                    <>
                      <div className="w-14 h-14 rounded-full bg-green-500 flex items-center justify-center animate-bounce">
                        <CheckCircle2 className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xl font-bold text-green-700">
                          {t?.lesson?.congrats || "Bravo !"} 🎉
                        </p>
                        <p className="text-green-600 text-sm">
                          {streak >= 3 
                            ? `${t?.lesson?.perfectStreak || "Série parfaite"} 🔥 x${streak}` 
                            : t?.lesson?.correctAnswer || "Bonne réponse"}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-green-600">+10</span>
                        <p className="text-xs text-green-500">XP</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-14 h-14 rounded-full bg-red-500 flex items-center justify-center animate-shake">
                        <X className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xl font-bold text-red-700">
                          {t?.lesson?.oops || "Oups !"} 😅
                        </p>
                        <p className="text-red-600 text-sm">
                          {t?.lesson?.tryAgain || "Ce n'est pas ça, mais tu apprendras !"}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Heart className="w-5 h-5 text-red-500 fill-red-500" />
                        <span className="text-red-600 font-bold">-1</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Bonne réponse (si erreur) */}
              {!isCorrect && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                  <p className="text-sm text-green-600 font-medium mb-1">
                    {t?.lesson?.theCorrectAnswer || "La bonne réponse était :"}
                  </p>
                  <p className="text-lg font-bold text-green-700">
                    ✓ {isReviewMode ? localizedWrongAnswers[reviewIndex]?.correct_answer : currentExercise?.correct_answer}
                  </p>
                </div>
              )}

              {/* Explication - Plus compacte */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <span className="text-xl">💡</span>
                  <div>
                    <p className="font-semibold text-blue-900 text-sm mb-1">
                      {t?.lesson?.didYouKnow || "Le savais-tu ?"}
                    </p>
                    <p className="text-blue-800 text-sm">
                      {isReviewMode ? localizedWrongAnswers[reviewIndex]?.explanation : currentExercise?.explanation}
                    </p>
                  </div>
                </div>
              </div>

              {/* Hearts restants */}
              <div className="flex items-center justify-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Heart
                    key={i}
                    className={`w-5 h-5 transition-all ${
                      i < (user?.hearts || 0)
                        ? 'text-red-500 fill-red-500'
                        : 'text-gray-200'
                    }`}
                  />
                ))}
                <span className="ml-2 text-sm text-gray-500">
                  {user?.hearts || 0}/5
                </span>
              </div>

              {/* Bouton Continuer */}
              <Button
                onClick={handleNextExercise}
                className={`w-full py-5 text-lg font-bold rounded-xl transition-all ${
                  isCorrect 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600' 
                    : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700'
                }`}
              >
                {isReviewMode ? (
                  reviewIndex < localizedWrongAnswers.length - 1 ? (
                    <>
                      {t?.lesson?.continueLabel || "Continuer"}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  ) : (
                    <>
                      {t?.lesson?.finishReview || "Terminer"}
                      <Trophy className="w-5 h-5 ml-2" />
                    </>
                  )
                ) : currentExerciseIndex < localizedExercises.length - 1 ? (
                  <>
                    {t?.lesson?.continueLabel || "Continuer"}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                ) : (
                  <>
                    {t?.lesson?.finish || "Terminer"}
                    <Trophy className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Lesson Info */}
        <div className="text-center mt-6 text-gray-600">
          <p className="text-sm">
            <strong>{localizedLesson?.title}</strong> • {correctAnswers}/{localizedExercises.length} {t.lesson.correctAnswers}
            {totalXPGained > 0 && ` • +${totalXPGained} ${t.lesson.xpGained}`}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LessonNew;
